import { type FC } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  DefinitionTooltip,
  type DefinitionTooltipInfo,
} from "src/components/common";
import {
  MetaGroupedBarLegend,
  META_CHART_METRIC_COLORS,
  META_GROUPED_BAR_COLORS,
  truncateMetaChartLabel,
  type MetaBarMetricKey,
  type MetaGroupedChartDatum,
} from "./MetaChartConfig";
import { OrganicChartTooltip } from "src/components/common/OrganicChartTooltip/OrganicChartTooltip";
import { formatPaidMediaPercent, formatPaidMediaRoas } from "../../utils/PaidMediaNumberFormat";
import { usePaidMediaChartRtl } from "../../utils/PaidMediaChartRtl";

interface MetaChartsRowProps {
  metaSectionHelp: (_key: string) => DefinitionTooltipInfo;
  campaignBarData: Array<{ name: string; roas: number }>;
  platformComparisonData: MetaGroupedChartDatum[];
  postTypeData: MetaGroupedChartDatum[];
  groupedBarLegendItems: ReadonlyArray<{ key: MetaBarMetricKey; label: string }>;
  barSeriesLabels: { spend: string; conversions: string; impressions: string; ctr: string };
  formatMoney: (
    _value: number,
    _options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
  ) => string;
  numberLocale: string;
  formatChartAxisCompactLocalized: (_value: unknown) => string;
}

const MetaChartsRow: FC<MetaChartsRowProps> = ({
  metaSectionHelp,
  campaignBarData,
  platformComparisonData,
  postTypeData,
  groupedBarLegendItems,
  barSeriesLabels,
  formatMoney,
  numberLocale,
  formatChartAxisCompactLocalized,
}) => {
  const { t } = useTranslation();
  const chartRtl = usePaidMediaChartRtl();

  return (
    <div className="paid-media-meta-charts">
      <div className="paid-media-meta-charts__primary" data-pdf-block="meta-charts-primary">
      <article className="paid-media-panel paid-media-panel--chart-stack">
        <div className="paid-media-panel__header">
          <div className="paid-media-panel__heading">
            <h3>{t("metaAdsReport.sections.roasByCampaign")}</h3>
            <DefinitionTooltip
              label={t("metaAdsReport.sections.roasByCampaign")}
              info={metaSectionHelp("roasByCampaign")}
            />
          </div>
        </div>
        <div className="paid-media-chart paid-media-chart--roas-stack">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={campaignBarData}
              barCategoryGap="16%"
              margin={chartRtl.margin({ top: 6, right: 12, bottom: 34, left: 0 })}
            >
              <CartesianGrid stroke="#e8edf4" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="name"
                interval={0}
                reversed={chartRtl.axisReversed}
                tickFormatter={value => truncateMetaChartLabel(value, 8)}
                tick={{ fontSize: 10, fill: "#7b8794" }}
                axisLine={false}
                tickLine={false}
                height={38}
              />
              <YAxis
                orientation={chartRtl.yAxisOrientation}
                tick={{ fontSize: 11, fill: "#60718b" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={formatChartAxisCompactLocalized}
              />
              <Tooltip
                content={({ active, label, payload }) => (
                  <OrganicChartTooltip
                    active={active}
                    label={label}
                    labelTitle={t("metaAdsReport.chart.campaign")}
                    payload={payload}
                    valueFormatter={(value, name) =>
                      name === "roas"
                        ? formatPaidMediaRoas(Number(value ?? 0))
                        : Number(value ?? 0).toLocaleString(numberLocale)
                    }
                  />
                )}
                wrapperStyle={{ pointerEvents: "none" }}
              />
              <Bar
                dataKey="roas"
                fill={META_CHART_METRIC_COLORS.spend}
                maxBarSize={42}
                radius={[5, 5, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </article>

      <article className="paid-media-panel paid-media-panel--chart-stack">
        <div className="paid-media-panel__header paid-media-panel__header--with-legend">
          <div className="paid-media-panel__heading">
            <h3>{t("metaAdsReport.sections.platformComparison")}</h3>
            <DefinitionTooltip
              label={t("metaAdsReport.sections.platformComparison")}
              info={metaSectionHelp("platformComparison")}
            />
          </div>
          <MetaGroupedBarLegend items={groupedBarLegendItems} />
        </div>
        <div className="paid-media-chart paid-media-chart--grouped">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={platformComparisonData}
              barGap={4}
              barCategoryGap="20%"
              margin={chartRtl.margin({ top: 6, right: 10, bottom: 8, left: 0 })}
              maxBarSize={28}
            >
              <CartesianGrid stroke="#e8edf4" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="label"
                reversed={chartRtl.axisReversed}
                tickFormatter={value => truncateMetaChartLabel(value, 14)}
                tick={{ fontSize: 11, fill: "#60718b" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                width={34}
                orientation={chartRtl.yAxisOrientation}
                tick={{ fontSize: 11, fill: "#60718b" }}
                axisLine={false}
                tickLine={false}
                domain={[0, 100]}
                tickFormatter={value => formatPaidMediaPercent(Number(value), 0)}
              />
              <Tooltip
                content={({ active, label, payload }) => (
                  <OrganicChartTooltip
                    active={active}
                    label={label}
                    labelTitle={t("metaAdsReport.chart.platform")}
                    payload={payload}
                    seriesLabelForDataKey={key => {
                      if (key === "ctr") {
                        return barSeriesLabels.ctr;
                      }
                      if (key === "impressions") {
                        return barSeriesLabels.impressions;
                      }
                      if (key === "conversions") {
                        return barSeriesLabels.conversions;
                      }
                      return barSeriesLabels.spend;
                    }}
                    valueFormatter={(value, name) => {
                      const row = platformComparisonData.find(item => item.label === label);
                      if (name === "spend") {
                        return formatMoney(row?.spend ?? Number(value ?? 0));
                      }
                      if (name === "ctr") {
                        return formatPaidMediaPercent(row?.ctr ?? Number(value ?? 0));
                      }
                      if (name === "impressions") {
                        return (row?.impressions ?? Number(value ?? 0)).toLocaleString(numberLocale);
                      }
                      if (name === "conversions") {
                        return (row?.conversions ?? Number(value ?? 0)).toLocaleString(numberLocale);
                      }
                      return Number(value ?? 0).toLocaleString(numberLocale);
                    }}
                  />
                )}
                wrapperStyle={{ pointerEvents: "none" }}
              />
              <Bar
                dataKey="spendValue"
                fill={META_GROUPED_BAR_COLORS.spend}
                barSize={28}
                radius={[5, 5, 0, 0]}
                name="spend"
              />
              <Bar
                dataKey="impressionsValue"
                fill={META_GROUPED_BAR_COLORS.impressions}
                barSize={28}
                radius={[5, 5, 0, 0]}
                name="impressions"
              />
              <Bar
                dataKey="conversionsValue"
                fill={META_GROUPED_BAR_COLORS.conversions}
                barSize={28}
                radius={[5, 5, 0, 0]}
                name="conversions"
              />
              <Bar
                dataKey="ctrValue"
                fill={META_GROUPED_BAR_COLORS.ctr}
                barSize={28}
                radius={[5, 5, 0, 0]}
                name="ctr"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </article>
      </div>

      <article className="paid-media-panel paid-media-panel--chart-stack" data-pdf-block="performance-by-post-type">
        <div className="paid-media-panel__header paid-media-panel__header--with-legend">
          <div className="paid-media-panel__heading">
            <h3>{t("metaAdsReport.sections.performanceByPostType")}</h3>
            <DefinitionTooltip
              label={t("metaAdsReport.sections.performanceByPostType")}
              info={metaSectionHelp("performanceByPostType")}
            />
          </div>
          <MetaGroupedBarLegend items={groupedBarLegendItems} />
        </div>
        <div className="paid-media-chart paid-media-chart--post-type">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={postTypeData}
              layout="vertical"
              barGap={4}
              barCategoryGap="38%"
              margin={chartRtl.margin({ top: 8, right: 14, bottom: 4, left: 4 })}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e8edf4" horizontal={false} />
              <YAxis
                dataKey="label"
                type="category"
                orientation={chartRtl.yAxisOrientation}
                tickFormatter={value => truncateMetaChartLabel(value, 14)}
                width={92}
                tick={{ fontSize: 12, fill: "#60718b" }}
                axisLine={false}
                tickLine={false}
              />
              <XAxis
                type="number"
                reversed={chartRtl.axisReversed}
                tick={{ fontSize: 11, fill: "#60718b" }}
                axisLine={false}
                tickLine={false}
                domain={[0, 100]}
                tickFormatter={value => formatPaidMediaPercent(Number(value), 0)}
              />
              <Tooltip
                content={({ active, label, payload }) => (
                  <OrganicChartTooltip
                    active={active}
                    label={label}
                    labelTitle={t("metaAdsReport.chart.postType")}
                    payload={payload}
                    seriesLabelForDataKey={key => {
                      if (key === "ctr") {
                        return barSeriesLabels.ctr;
                      }
                      if (key === "impressions") {
                        return barSeriesLabels.impressions;
                      }
                      if (key === "conversions") {
                        return barSeriesLabels.conversions;
                      }
                      return barSeriesLabels.spend;
                    }}
                    valueFormatter={(value, name) => {
                      const row = postTypeData.find(item => item.label === label);
                      if (name === "spend") {
                        return formatMoney(row?.spend ?? Number(value ?? 0));
                      }
                      if (name === "ctr") {
                        return formatPaidMediaPercent(row?.ctr ?? Number(value ?? 0));
                      }
                      if (name === "impressions") {
                        return (row?.impressions ?? Number(value ?? 0)).toLocaleString(numberLocale);
                      }
                      if (name === "conversions") {
                        return (row?.conversions ?? Number(value ?? 0)).toLocaleString(numberLocale);
                      }
                      return Number(value ?? 0).toLocaleString(numberLocale);
                    }}
                  />
                )}
                wrapperStyle={{ pointerEvents: "none" }}
              />
              <Bar
                dataKey="spendValue"
                fill={META_GROUPED_BAR_COLORS.spend}
                barSize={10}
                background={{ fill: "#f5f7fb", radius: 5 }}
                radius={chartRtl.horizontalBarRadius(5)}
                name="spend"
              />
              <Bar
                dataKey="impressionsValue"
                fill={META_GROUPED_BAR_COLORS.impressions}
                barSize={10}
                background={{ fill: "#f5f7fb", radius: 5 }}
                radius={chartRtl.horizontalBarRadius(5)}
                name="impressions"
              />
              <Bar
                dataKey="conversionsValue"
                fill={META_GROUPED_BAR_COLORS.conversions}
                barSize={10}
                background={{ fill: "#f5f7fb", radius: 5 }}
                radius={chartRtl.horizontalBarRadius(5)}
                name="conversions"
              />
              <Bar
                dataKey="ctrValue"
                fill={META_GROUPED_BAR_COLORS.ctr}
                barSize={10}
                background={{ fill: "#f5f7fb", radius: 5 }}
                radius={chartRtl.horizontalBarRadius(5)}
                name="ctr"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </article>
    </div>
  );
};

export default MetaChartsRow;
